/**
 * THE TUTORIAL'S CONTENT — chapters, Journal goals and Discoveries, as DATA. This is the file a change to WHAT the
 * player is taught touches; the engine (engine.ts) and the UI (src/ui/guide/) do not change for a new step.
 *
 * THE DESIGN (Shelf Garden Kickstarter/tutorial-plan.html):
 *   • CHAPTER 1, FIRST HARVEST — a short guided golden path through the core loop: greenhouse → seeds → plant →
 *     water → fish while it grows → harvest → sell. Steps form a GRAPH (`after`), not a line, and finish on GAME
 *     EVENTS (events.ts), never on a click. `alreadyDone` finishes a step silently when the world already shows it
 *     done — that is what makes reordering steps, migrating saves and players who wander ahead safe.
 *   • CHAPTER 2 lives in the GARDEN JOURNAL as goals, any order, each with progress and a "Show me".
 *   • DISCOVERIES — one short tip the first time a feature unlocks or appears, never chained.
 *
 * COPY RULE: one instruction of about ten words or fewer (guide.test.ts enforces a ceiling). The player DOES, the
 * text only points. A step's `anchor` is a PATH: the deepest anchor currently on screen is the one highlighted, so
 * "Tools → Plant → a pot" walks itself as the menu opens and the tool engages.
 */
import { label, path } from "./labels";
import type { GuideAnchorId } from "./anchors";
import type { GuideEvent, GuideEventType, GuidePanel } from "./events";

/** The small read-only summary of the game the guide is allowed to see (built by ONE adapter, src/ui/guide). */
export interface GuideWorld {
  scene: string;
  coins: number;
  /** seeds in the bag, all species */
  seedsOwned: number;
  /** greenhouse pots with a plant in them */
  potsPlanted: number;
  /** of those, ripe now */
  ripePots: number;
  /** lifetime harvests */
  harvests: number;
  fishCaught: number;
  bugsCaught: number;
  /** insects on screen right now (garden) */
  bugsVisible: number;
  /** decor placed in the garden, the starter oak included */
  decorPlaced: number;
  waterCells: number;
  plotCells: number;
  gardenLevel: number;
  fishLevel: number;
  bugLevel: number;
  /** coins the next garden plot costs (Infinity at the cap) */
  nextPlotPrice: number;
  /** is the Kickstarter level cap reached on any track */
  atDemoCap: boolean;
  /** today's daily check-in square can be claimed */
  checkinReady: boolean;
}

export type EventMatch = { event: GuideEventType; where?: (e: GuideEvent) => boolean };
export type Gesture = "tap" | "drag" | "hold";

/** A hand's path from the centre of the anchor the ring is on: to another anchor's centre (which may still be WAITING —
 *  see src/ui/guide/anchors.ts), or a stroke of `dx, dy` screen px (`centred` puts the stroke's middle on the anchor
 *  rather than its start). `mouseOnly`: touch screens do something else there (the Market's select-then-tap). */
export type GesturePath = { to: GuideAnchorId; mouseOnly?: boolean } | { dx: number; dy: number; centred?: boolean };

export interface GuideStep {
  /** STABLE FOREVER — saves store ids, never positions */
  id: string;
  /** a short name for lists — the Journal's checklist, the admin's "start the test save here" buttons */
  name: string;
  /** must be done first (default: none) */
  after?: string[];
  /** only offered while this is true (e.g. a plant is ripe) */
  showWhen?: (w: GuideWorld) => boolean;
  /** how it completes */
  doneOn: EventMatch[];
  /** the world already shows it done → completes silently */
  alreadyDone?: (w: GuideWorld) => boolean;
  /** where to point; the deepest anchor on screen wins */
  anchor?: GuideAnchorId[];
  /** the instruction (≈10 words), and an optional variant for touch screens */
  text: string;
  touchText?: string;
  /** a second line while a listed panel is open (e.g. inside the Market), with an optional touch-screen wording */
  inPanel?: { panel: GuidePanel; text: string; touchText?: string };
  /** one-line hints shown as the step's own events arrive (fishing's cast → bite → hook) */
  hints?: Partial<Record<GuideEventType, string>>;
  /** BUSY from any `on` event until an `off` event (fishing: from the cast until the line is back in). A busy step on
   *  screen is never preempted by a higher-priority one — the player is mid-action and the bubble stays with them;
   *  the step waiting behind it shows only as its `queuedNote`. Only the step ON SCREEN goes busy, and it is not
   *  saved: a reload ends the action anyway. */
  busy?: { on: GuideEventType[]; off: GuideEventType[] };
  /** one line under a BUSY step's hint while this step waits behind it ("your flower is ready…") */
  queuedNote?: string;
  gesture?: Gesture;
  /** where the hand DRAGS, per anchor: while the ring is on a key anchor the hand travels that path (with a faint
   *  trail, looping with a pause) instead of doing `gesture` in place. A step with paths TAPS on its other anchors —
   *  those are the menu walk that leads to the drag, and a hand wiggling sideways over a button teaches nothing. */
  gesturePath?: Partial<Record<GuideAnchorId, GesturePath>>;
  /** panels the step may show inside of (anything else open hides the guide until it closes) */
  panels?: GuidePanel[];
  /** a larger number wins when several steps are available */
  priority?: number;
  /** a centred card instead of a pointer (the Welcome) */
  card?: boolean;
  /** other wording while the world says so — the first match replaces `text` / `touchText` */
  variants?: Array<{ when: (w: GuideWorld) => boolean; text: string; touchText?: string }>;
}

export interface GuideChapter {
  id: string;
  title: string;
  steps: GuideStep[];
  /** what finishing (or skipping) it pays — granted by the game, once */
  reward: { seeds?: Record<string, number>; decor?: Record<string, number>; label: string };
  /** the card's headline when it completes */
  doneText: string;
  /** THE LAST CHAPTER's card: the wrap-up that tells the player they have the basics */
  finale?: { title: string; text: string };
}

/** The first planting while Chapter 1 runs ripens in this many seconds (decision 2 of the plan). */
export const FIRST_CROP_SECONDS = 60;
/** …but never more than this much growth is gifted (a Daisy is 240 s → 60 s; a slower first pick just gets the
 *  same three minutes off). The server allows exactly this much on a never-harvested account's fresh sowing —
 *  `FIRST_CROP_HEADSTART_SECONDS` in server/index.js mirrors it and server/save.test.js fails if they drift. */
export const FIRST_CROP_HEADSTART_SECONDS = 180;
/** If the player has fewer coins than this and no seeds, Chapter 1 gives them a few Daisy seeds. */
export const SEED_GIFT_BELOW_COINS = 15;
export const SEED_GIFT = { daisy: 3 };

const sceneIs = (scene: string) => (e: GuideEvent) => e.type === "scene.entered" && e.scene === scene;

export const FIRST_HARVEST: GuideChapter = {
  id: "first-harvest",
  title: "First Harvest",
  doneText: "You grew, caught and sold your first harvest!",
  reward: { seeds: { daisy: 5 }, decor: { pinwheel: 1 }, label: "5 Daisy seeds and a Pinwheel" },
  steps: [
    { id: "welcome", name: "Welcome", card: true, doneOn: [{ event: "guide.welcome" }],
      text: "This is your Shelf Garden. Time to grow your first flower!" },
    { id: "look", name: "Look around", after: ["welcome"], doneOn: [{ event: "camera.panned" }, { event: "scene.entered", where: sceneIs("greenhouse") }],
      anchor: ["garden.center"], gesture: "drag", gesturePath: { "garden.center": { dx: 220, dy: 0, centred: true } },
      text: "Drag the ground to look around.", touchText: "Drag the ground with one finger to look around." },
    { id: "to-greenhouse", name: "Into the greenhouse", after: ["look"], doneOn: [{ event: "scene.entered", where: sceneIs("greenhouse") }],
      alreadyDone: (w) => w.scene === "greenhouse",
      anchor: ["nav.greenhouse"], gesture: "tap",
      text: "Your greenhouse is ready. Step inside!" },
    { id: "buy-seeds", name: "Buy seeds", after: ["to-greenhouse"], doneOn: [{ event: "seed.bought" }],
      alreadyDone: (w) => w.seedsOwned > 0 || w.potsPlanted > 0 || w.harvests > 0,
      // the Daisy tile, then Buy One once it is lifted or selected (the zone's anchor waits until then)
      anchor: ["bar.shop", "market.seed.daisy", "market.buyOne"], gesture: "tap", panels: ["market"],
      gesturePath: { "market.seed.daisy": { to: "market.buyOne", mouseOnly: true } },
      text: `Open the ${label("bar.shop")} and buy Daisy seeds.`,
      inPanel: { panel: "market", text: `Drag Daisy seeds onto ${label("market.buyOne")}.`,
        touchText: `Tap Daisy seeds, then tap ${label("market.buyOne")}.` } },
    { id: "plant", name: "Plant", after: ["buy-seeds"], doneOn: [{ event: "seed.planted" }],
      alreadyDone: (w) => w.potsPlanted > 0 || w.harvests > 0,
      anchor: ["nav.greenhouse", "bar.tools", "tools.plant", "seedtray.seeds", "pot.firstEmpty"], gesture: "tap",
      text: `Open ${path("bar.tools", "tools.plant")}, pick Daisy, then click an empty pot.`,
      touchText: `Open ${path("bar.tools", "tools.plant")}, pick Daisy, then tap an empty pot.` },
    { id: "water", name: "Water", after: ["plant"], doneOn: [{ event: "pot.watered" }],
      alreadyDone: (w) => w.harvests > 0,
      anchor: ["nav.greenhouse", "bar.tools", "tools.water", "pot.planted"], gesture: "hold",
      text: `Open ${path("bar.tools", "tools.water")}, then hold the can over your pot.`,
      touchText: `Open ${path("bar.tools", "tools.water")}, then hold your finger on the pot.` },
    { id: "fish", name: "Catch a fish", after: ["water"], doneOn: [{ event: "fish.caught" }],
      alreadyDone: (w) => w.fishCaught > 0,
      anchor: ["nav.garden", "bar.fish", "fish.rod", "pond"], gesture: "tap",
      text: `Catch a fish while it grows: open ${path("bar.fish", "fish.rod")}, click the pond.`,
      touchText: `Catch a fish while it grows: open ${path("bar.fish", "fish.rod")}, tap the pond.`,
      // the Daisy ripens in about a minute, usually mid-lesson: it waits (Harvest comes AFTER this step — a ripe flower
      // used to jump ahead and cut the fishing lesson short). A player who cut it anyway gets the "in" wording.
      variants: [
        { when: (w) => w.harvests > 0, text: `Your flower is in! Now open ${path("bar.fish", "fish.rod")}, click the pond.`,
          touchText: `Your flower is in! Now open ${path("bar.fish", "fish.rod")}, tap the pond.` },
        { when: (w) => w.ripePots > 0, text: "Your flower is ready! Catch a fish first, then harvest it." },
      ],
      hints: {
        "fish.cast": "Wait for a bite…",
        "fish.bite": "A bite! Click or tap now to hook it.",
        "fish.hooked": "Pull toward the arrow to tire it out. Don't snap the line.",
        "fish.lost": "It got away. Cast again!",
        "fish.reeled": "Line's in. Cast again when you're ready.",
      },
      // a line in the water holds the bubble against any step that could jump ahead of it
      busy: { on: ["fish.cast", "fish.bite", "fish.hooked"], off: ["fish.caught", "fish.lost", "fish.reeled"] } },
    // AFTER the fish, not beside it: the first crop ripens in a minute, and a Harvest that jumped ahead (priority) took
    // the bubble mid-lesson — the fishing walkthrough was the part players lost. Cut early anyway, it completes itself.
    { id: "harvest", name: "Harvest", after: ["fish"], showWhen: (w) => w.ripePots > 0 || w.harvests > 0,
      doneOn: [{ event: "plant.harvested" }], alreadyDone: (w) => w.harvests > 0,
      anchor: ["nav.greenhouse", "bar.tools", "tools.harvest", "pot.ripe"], gesture: "tap",
      text: `Your flower is ready! Open ${path("bar.tools", "tools.harvest")}, then click it.`,
      touchText: `Your flower is ready! Open ${path("bar.tools", "tools.harvest")}, then tap it.` },
    { id: "sell", name: "Sell", after: ["harvest", "fish"], doneOn: [{ event: "item.sold" }],
      // the cut Daisy in the bag, then Sell One once it is lifted or selected; the zone row if the bag has no Daisy
      anchor: ["bar.shop", "market.sellZone", "market.bag.daisy", "market.sellOne"], gesture: "tap", panels: ["market"],
      gesturePath: { "market.bag.daisy": { to: "market.sellOne", mouseOnly: true } },
      text: `Sell your flower in the ${label("bar.shop")} to earn coins.`,
      inPanel: { panel: "market", text: `Drag your flower onto ${label("market.sellOne")}.`,
        touchText: `Tap your flower, then tap ${label("market.sellOne")}.` } },
  ],
};

/**
 * CHAPTER 2, MAKE IT YOURS — the garden's other basics, guided the same way: decorate (with First Harvest's Pinwheel),
 * paint water, see where the garden grows, catch a bug, and find it in the Field Guide. Its card is the FINALE: the
 * player has the basics and the game is theirs to explore. Nothing here costs coins — a new player cannot afford a
 * plot yet, so that step shows where expansion lives rather than asking for a purchase.
 */
export const MAKE_IT_YOURS: GuideChapter = {
  id: "make-it-yours",
  title: "Settling In",
  doneText: "Your garden is taking shape.",
  reward: { decor: { lantern: 1 }, label: "a Garden Lantern" },
  finale: {
    title: "You've learned the basics!",
    text: "That covers the basics. Your Garden Journal has goals and tips for what to do next.",
  },
  steps: [
    { id: "decorate", name: "Decorate", doneOn: [{ event: "decor.placed" }], alreadyDone: (w) => w.decorPlaced > 1,
      anchor: ["nav.garden", "bar.decorate", "decorate.pinwheel", "garden.placeSpot"], gesture: "drag", panels: ["decorate"],
      // the Pinwheel tile carried OUT of the palette onto the lawn
      gesturePath: { "decorate.pinwheel": { to: "garden.center" } },
      text: `Open ${label("bar.decorate")} and drag your Pinwheel onto the lawn.` },
    { id: "paint-water", name: "Paint water", after: ["decorate"], doneOn: [{ event: "water.painted" }],
      anchor: ["nav.garden", "bar.build", "build.waterPainter", "pond.edge"], gesture: "drag",
      // a short stroke across the shore, out of the water onto the grass
      gesturePath: { "pond.edge": { dx: 96, dy: 48, centred: true } },
      text: `Open ${path("bar.build", "build.waterPainter")}, then drag to grow your pond.` },
    { id: "see-plots", name: "See the plots", after: ["paint-water"], alreadyDone: (w) => w.plotCells > 1,
      doneOn: [{ event: "tool.opened", where: (e) => e.type === "tool.opened" && e.tool === "expand" }, { event: "plot.bought" }],
      anchor: ["nav.garden", "bar.build", "build.plots"], gesture: "tap",
      text: `Open ${path("bar.build", "build.plots")} to see where your garden can grow.` },
    // done by a CATCH or by a real attempt that FAILS: the lesson is the net, not the luck of the roll. Picking the
    // net up here sends one pinned tutorial bug (useGarden), which stays until one of the two happens.
    { id: "catch-bug", name: "Catch a bug", after: ["see-plots"], doneOn: [{ event: "bug.caught" }, { event: "bug.missed" }],
      alreadyDone: (w) => w.bugsCaught > 0,
      anchor: ["nav.garden", "bar.bugs", "bugs.net", "bug.live"], gesture: "hold",
      text: `Open ${path("bar.bugs", "bugs.net")}, then hold the net over the bug.`,
      touchText: `Open ${path("bar.bugs", "bugs.net")}, then hold your finger on the bug.` },
    { id: "field-guide", name: "Field Guide", after: ["catch-bug"], panels: ["fieldGuide"],
      doneOn: [{ event: "panel.opened", where: (e) => e.type === "panel.opened" && e.panel === "fieldGuide" }],
      anchor: ["bar.menu", "menu.guide"], gesture: "tap",
      text: `Open ${path("bar.menu", "menu.guide")}. Every bug and fish you find is logged.`,
      // the bug got away and nothing is logged yet — the copy must not claim a find
      variants: [{ when: (w) => w.bugsCaught === 0, text: `Open ${path("bar.menu", "menu.guide")}. Every bug you catch is logged there.` }] },
  ],
};

export const CHAPTERS: GuideChapter[] = [FIRST_HARVEST, MAKE_IT_YOURS];

/** CHAPTER 2 — the Journal's goals. Shown three at a time, in this order, once Chapter 1 is done. */
export interface GuideGoal {
  id: string;
  icon: string;
  text: string;
  doneOn?: EventMatch[];
  /** current / target, for the progress bar; done when current ≥ target */
  progress?: (w: GuideWorld) => [number, number];
  /** "Show me" points along this path */
  anchor?: GuideAnchorId[];
}

export const GOALS: GuideGoal[] = [
  { id: "goal.harvest10", icon: "🌼", text: "Harvest 10 plants",
    progress: (w) => [w.harvests, 10], anchor: ["nav.greenhouse", "bar.tools", "tools.harvest"] },
  { id: "goal.level3", icon: "⭐", text: "Reach Gardening level 3 to unlock a second rack",
    progress: (w) => [w.gardenLevel, 3], anchor: ["levels.garden"] },
  { id: "goal.decor5", icon: "🪴", text: "Place 5 decorations in your garden",
    progress: (w) => [Math.max(0, w.decorPlaced - 1), 5], anchor: ["nav.garden", "bar.decorate"] },
  { id: "goal.bugs5", icon: "🦋", text: "Catch 5 bugs",
    progress: (w) => [w.bugsCaught, 5], anchor: ["nav.garden", "bar.bugs", "bugs.net"] },
  { id: "goal.plot", icon: "🗺️", text: "Expand your garden with a new plot",
    doneOn: [{ event: "plot.bought" }], progress: (w) => [w.plotCells > 1 ? 1 : 0, 1],
    anchor: ["nav.garden", "bar.build", "build.plots"] },
  { id: "goal.fish5", icon: "🎣", text: "Catch 5 fish",
    progress: (w) => [w.fishCaught, 5], anchor: ["nav.garden", "bar.fish", "fish.rod"] },
];
export const GOALS_AT_ONCE = 3;

/** DISCOVERIES — fired once, when their trigger first happens (an event, or a world condition turning TRUE). */
export interface GuideDiscovery {
  id: string;
  icon: string;
  title: string;
  text: string;
  on?: EventMatch;
  /** fires on the false → true EDGE only — unless `atLoad`, when already being true counts too */
  when?: (w: GuideWorld) => boolean;
  atLoad?: boolean;
  anchor?: GuideAnchorId[];
}

const levelUp = (track: string, level: number): EventMatch => ({
  event: "level.up", where: (e) => e.type === "level.up" && e.track === track && e.level >= level,
});

export const DISCOVERIES: GuideDiscovery[] = [
  { id: "d.bugs", icon: "🦋", title: "A visitor!", text: `Bugs land in your garden. To catch one, hold the ${label("bugs.net")} over it.`,
    when: (w) => w.scene === "garden" && w.bugsVisible > 0, anchor: ["bar.bugs", "bugs.net"] },
  { id: "d.rack2", icon: "🪴", title: "A second rack", text: "You can buy another rack of 12 pots. Look for the sign in the greenhouse.",
    on: levelUp("garden", 3), anchor: ["nav.greenhouse", "sign.buyRack"] },
  { id: "d.harvestAll", icon: "🧺", title: label("tools.harvestAll"), text: `Cut every ripe flower at once from ${path("bar.tools", "tools.harvestAll")}.`,
    on: levelUp("garden", 11), anchor: ["nav.greenhouse", "bar.tools", "tools.harvestAll"] },
  { id: "d.compost", icon: "♻️", title: "Compost", text: "Buy the Composter from the greenhouse terminal. Compost makes pots grow better.",
    on: levelUp("garden", 12), anchor: ["nav.greenhouse", "terminal"] },
  { id: "d.lures", icon: "🪝", title: "Lures", text: `Lures change what bites. Find them under ${path("bar.fish", "fish.tackle")}.`,
    on: levelUp("fish", 3), anchor: ["nav.garden", "bar.fish", "fish.tackle"] },
  { id: "d.talents", icon: "✨", title: label("bugs.talents"), text: `Pick your first talent in ${path("bar.bugs", "bugs.talents")}. You can swap it any time.`,
    on: levelUp("bug", 5), anchor: ["nav.garden", "bar.bugs", "bugs.talents"] },
  { id: "d.fieldGuide", icon: "📖", title: "Field Guide", text: `Every new species is logged in your Field Guide (${path("bar.menu", "menu.guide")}).`,
    on: { event: "bug.caught", where: (e) => e.type === "bug.caught" && e.first }, anchor: ["bar.menu", "menu.guide"] },
  { id: "d.clues", icon: "🍾", title: "A clue", text: "Bottles and notes reveal when rare fish and bugs appear. Check the Field Guide.",
    on: { event: "pickup.caught" }, anchor: ["bar.menu", "menu.guide"] },
  { id: "d.achievements", icon: "🏆", title: "Achievements", text: `Achievements pay coins and Petals. See them all in ${path("bar.menu", "menu.achievements")}.`,
    on: { event: "achievement.unlocked" }, anchor: ["bar.menu", "menu.achievements"] },
  { id: "d.plot", icon: "🗺️", title: "More land", text: `You can afford a new garden plot. Buy it from ${path("bar.build", "build.plots")}.`,
    when: (w) => w.coins >= w.nextPlotPrice && w.plotCells < 2, anchor: ["nav.garden", "bar.build", "build.plots"] },
  { id: "d.checkin", icon: "🗓️", title: "Daily gift", text: "You can claim a gift every day from the calendar at the top.",
    when: (w) => w.checkinReady, atLoad: true, anchor: ["hud.checkin"] },
  { id: "d.demoCap", icon: "🌱", title: "The demo's level cap", text: "That's as far as the Kickstarter demo goes. Your garden carries over to the full game.",
    when: (w) => w.atDemoCap, atLoad: true, anchor: ["levels.garden"] },
];

/** Discoveries are spaced out: at most one every this many ms. */
export const DISCOVERY_GAP_MS = 45_000;
/** A step's instruction longer than this many words fails guide.test.ts (a Discovery may run to MAX_TIP_WORDS). */
export const MAX_COPY_WORDS = 14;
export const MAX_TIP_WORDS = 22;
